/**
 * Enumeration for form controls
 * [textbox | textarea | formBuilder]
 * @enum
 */
export enum ControlType {
    TextBox,
    TextArea,
    FormulaBuilder,
}

/**
 * Enumeration for translation form status
 * [Valid]
 * @enum
 */
 export enum TranslationFormStatus {
    Valid = 'VALID'
}

/**
 * @description
 * Translation form status invalid values.
 */
 export class TranslationFormChangeStatus {
    valid: boolean;
}

/**
 * @description
 * Translation table header which displays Label, default language and dropdown with default select option
 * @example
 * ```
 * {
 *      "selectLanguageKey": "Admin.CountryTranslation.SelectLanguage",
 *      "labelKey": "Admin.CountryTranslation.Column.Label",
 *      "defaultLanguageKey": "Admin.CountryTranslation.Column.DefaultLanguage"
 * }
 * ```
 */
export class TranslationHeader {
    public selectLanguageKey: string;
    public labelKey: string;
    public defaultLanguageKey: string;
}

/**
 * @description
 * Create translation section with label key and
 * array of translation fields
 *
 * @usage
 * ```
 * const infoSection = new TranslationSection();
 * infoSection.labelKey = 'Admin.CountryTranslation.CountryInfo';
 * infoSection.translationFields = [];
 *
 * @example
 * ```
 *  [
 *      {
 *          "labelKey": "Country/Region Info",
 *          "translationFields": []
 *      }
 * ...
 *]
 * ```
 */
export class TranslationSection {
    public labelKey: string;
    public translationFields: Array<TranslationField>;
}

/**
 * @description
 * Create Translation Field(s) by dictionary id
 *
 * @usage
 * ```
 * const name = new TranslationField();
 * name.key = 'Admin.CountryTranslation.CountryInfo.Name';
 * name.defaultValue = countryTranslations.translatedValue; /// Germany
 *```
 * @example
 * ```
 * {
 *      "type": "textbox",
 *      "translations": {},
 *      "key": "Admin.CountryTranslation.CountryInfo.Name",
 *      "defaultValue": "Germany".
 *      ...
 * }
 * ```
 */
export class TranslationField {
    /** Translation field key */
    public key: string;
    /**
     * Reference details for translation field.
     * Details in the form of key/value pair associated with translation field.
     *
     * @example
     * ```
     * "translationFields": [
     *      {
     *          ...
     *           "reference" : {
     *               "fieldType" : 1
     *           },
     *           "defaultValue": "Sub Category",
     *          ...
     *      },
     *      {
     *          ...
     *           "reference" : {
     *               "fieldType" : 2
     *           },
     *           "defaultValue": "Sub Category Description",
     *          ...
     *      },
     * ```
     */
    public reference?: { [key: string]: string | number };
    /** @property Default value of traslation field */
    public defaultValue: string;
    /**
     * ControlType [textbox | textarea | formBuilder]
     * @defaults ControlType.textbox
     */
    public type?: ControlType = ControlType.TextBox;

    /**
     * Field identifier associated with the translation field.
     * @example
     * ```
     * name.field = PromotionTranslationField.Name;
     * ```
     */
    public field?: number;

    /** max length validation. Default is 250 */
    public maxlength = 250;
    /** name attribute for form controls [textbox | textarea | formBuilder] */
    public namePrefix: string;
    /** id prefix for form controls [textbox | textarea | formBuilder] id */
    public idPrefix: string;
    /**
     * Translations object with key as dictionary id and value as translation value
     * @example
     * ```
     * {
     *      "2": "Allemagne de l'Ouest",
     *      "3": "West-Duitsland"
     * }
     * ```
     */
    public translations: { [key: number]: string } = {};
}
